import React, { useState } from 'react';
import axios from 'axios';
import { AlertCircle, ChevronDown } from 'lucide-react';
import { REGION_GROUPS, REGION_CODES, REGION_ICONS } from '../utils/regions';

const SummonerPage = () => {
  const [gameName, setGameName] = useState('');
  const [tagLine, setTagLine] = useState('');
  const [mode, setMode] = useState('Recent'); // 'Recent' or 'Mastery'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [championData, setChampionData] = useState([]);
  const [patchNotes, setPatchNotes] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState('North America');
  const [isRegionDropdownOpen, setIsRegionDropdownOpen] = useState(false);
  const [hasSearched, setHasSearched] = useState(false); // Track if a search has been attempted

  // Helper function to get user-friendly error message
  const getErrorMessage = (error, mode) => {
    // If the error has a code property from our backend
    if (error.code) {
      switch (error.code) {
        case 'SUMMONER_NOT_FOUND':
          return 'Summoner not found. Please check the Game Name and Tag Line.';
        case 'NO_RECENT_GAMES':
          return 'This summoner has no recent games. Try checking their Champion Mastery instead.';
        case 'NO_MASTERY_DATA':
          return 'No champion mastery data found for this summoner.';
        case 'RATE_LIMIT':
          return 'Too many requests. Please try again in a few minutes.';
        case 'AUTH_ERROR':
          return 'Server authentication error. Please try again later.';
        case 'NETWORK_ERROR':
          return 'Network error. Please check your internet connection.';
        case 'INVALID_CHARACTERS':
          return 'Invalid characters in Game Name or Tag Line.';
        default:
          return error.details || error.message || 'An unexpected error occurred.';
      }
    }

    // Handle HTTP status codes
    if (error.status) {
      switch (error.status) {
        case 404:
          return mode === 'Recent'
            ? 'No recent games found for this summoner. Try checking their Champion Mastery instead.'
            : 'No mastery data found for this summoner.';
        case 429:
          return 'Too many requests. Please try again in a few minutes.';
        case 403:
          return 'Server authentication error. Please try again later.';
        default:
          return error.message || 'An unexpected error occurred.';
      }
    }

    return error.message || 'An unexpected error occurred.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!gameName || !tagLine || !selectedRegion) {
      setError('Please enter game name, tag line, and select a region.');
      return;
    }
    setHasSearched(true);

    setError('');
    setLoading(true);
    setPatchNotes(null);
    setChampionData([]);

    try {
      // First get the champion data based on mode
      const endpoint = mode === 'Mastery' ? '/api/summoner-mastery' : '/api/summoner';
      console.log('Calling endpoint:', endpoint);

      const region = REGION_CODES[selectedRegion];
      const response = await fetch(endpoint + `?gameName=${encodeURIComponent(gameName)}&tagLine=${encodeURIComponent(tagLine)}&region=${encodeURIComponent(region)}`);
      console.log('Using region:', region);
      const data = await response.json();

      if (!response.ok) {
        const error = new Error(data.error || 'Failed to fetch summoner data');
        error.status = response.status;
        error.code = data.code;
        error.details = data.details;
        error.mode = mode; // Add mode to error object
        throw error;
      }

      if (!Array.isArray(data)) {
        console.error('Unexpected response format:', data);
        throw new Error('Invalid response format from server');
      }

      // Handle empty data differently based on mode
      if (data.length === 0) {
        if (mode === 'Recent') {
          // For Recent mode, always show the error with suggestion to check Mastery
          const error = new Error('No recent games found for this summoner. Try checking their Champion Mastery instead.');
          error.code = 'NO_RECENT_GAMES';
          error.mode = mode;
          throw error;
        } else {
          // For Mastery mode, just set empty data without error
          // This will show the 'No changes' message in the UI
          setChampionData([]);
          return;
        }
      }

      // Validate champion data structure
      const validChampions = data.filter(champion => {
        if (!champion || typeof champion !== 'object') {
          console.warn('Invalid champion entry:', champion);
          return false;
        }
        if (!champion.name || !champion.last_played) {
          console.warn('Missing required fields in champion:', champion);
          return false;
        }
        return true;
      });

      if (validChampions.length === 0) {
        const error = new Error('Invalid champion data received');
        error.code = 'INVALID_DATA';
        throw error;
      }

      console.log('Valid champions:', validChampions);

      // Format the champions data
      const championsPayload = validChampions.map(({ name, last_played }) => ({
        champion: name,
        lastPlayed: last_played
      }));

      console.log('Fetching patch notes for champions:', championsPayload);
      
      const patchNotesResponse = await fetch(`/api/patch-notes?champions=${encodeURIComponent(JSON.stringify(championsPayload))}`);
      const patchNotesData = await patchNotesResponse.json();

      if (!patchNotesResponse.ok) {
        console.warn('Failed to fetch patch notes:', patchNotesData.error);
        // Don't throw here, just continue without patch notes
      } else {
        console.log('Received patch notes:', patchNotesData);
        setPatchNotes(patchNotesData);
      }
      
      setChampionData(validChampions);
    } catch (error) {
      console.error('Error:', error);
      setError(getErrorMessage(error, mode));
      setPatchNotes(null);
      setChampionData([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="container mx-auto p-4">
      
      <form onSubmit={handleSubmit} className="mb-8">
        <div className="flex flex-col space-y-4 w-full max-w-2xl mx-auto">
          {/* Region Selector */}
          <div className="relative w-full">
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Region
            </label>
            <button
              type="button"
              onClick={() => setIsRegionDropdownOpen(!isRegionDropdownOpen)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            >
              <span className="flex items-center">
                <span className="mr-2">{REGION_ICONS[selectedRegion]}</span>
                {selectedRegion}
              </span>
              <ChevronDown className="w-4 h-4" />
            </button>
            {isRegionDropdownOpen && (
              <div className="absolute z-10 w-full mt-1 bg-gray-700 border border-gray-600 rounded-md shadow-lg max-h-96 overflow-y-auto">
                {Object.entries(REGION_GROUPS).map(([group, regions]) => (
                  <div key={group} className="px-2 py-1">
                    <div className="text-xs text-gray-400 px-2 py-1">{group}</div>
                    {regions.map((region) => (
                      <button
                        key={region}
                        type="button"
                        onClick={() => {
                          setSelectedRegion(region);
                          setIsRegionDropdownOpen(false);
                        }}
                        className="w-full text-left px-2 py-1 hover:bg-gray-600 rounded flex items-center space-x-2"
                      >
                        <span>{REGION_ICONS[region]}</span>
                        <span>{region}</span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Game Name and Tag Line Section */}
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 w-full">
            {/* Game Name Input */}
            <div className="w-full sm:w-64">
              <label htmlFor="gameName" className="block text-sm font-medium text-gray-300 mb-1">
                Game Name
              </label>
              <input
                type="text"
                name="gameName"
                id="gameName"
                value={gameName}
                onChange={(e) => setGameName(e.target.value)}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-100"
                placeholder="Enter your game name"
                disabled={loading}
              />
            </div>

            {/* Tag Line Input */}
            <div className="w-full sm:w-64">
              <label htmlFor="tagLine" className="block text-sm font-medium text-gray-300 mb-1">
                Tag Line
              </label>
              <input
                type="text"
                name="tagLine"
                id="tagLine"
                value={tagLine}
                onChange={(e) => setTagLine(e.target.value)}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-100"
                placeholder="Enter your tag line"
                disabled={loading}
              />
            </div>

          {/* Mode Toggle */}
          <div className="w-full sm:w-auto">
            <div className="flex items-center bg-gray-800 rounded-md p-1 border border-gray-700 h-[42px]">
              <button
                type="button"
                onClick={() => setMode('Recent')}
                className={`px-4 rounded-md transition-all duration-200 ${
                  mode === 'Recent'
                    ? 'bg-blue-600 text-white font-medium'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <span style={{ fontFamily: 'BeaufortForLoL, sans-serif' }}>Recent</span>
              </button>
              <button
                type="button"
                onClick={() => setMode('Mastery')}
                className={`px-4 rounded-md transition-all duration-200 ${
                  mode === 'Mastery'
                    ? 'bg-blue-600 text-white font-medium'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <span style={{ fontFamily: 'BeaufortForLoL, sans-serif' }}>Mastery</span>
              </button>
            </div>
          </div>
        </div>
          {/* Submit Button Section */}
          <div className="flex justify-center mt-4">
            <button
              type="submit"
              className={`px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-colors
                ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Search Changes'}
            </button>
          </div>
        </div>
      </form>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center justify-center">
          <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
          <div className="flex flex-col items-center">
            <span className="text-sm font-medium">{error}</span>
            {mode === 'Recent' && error.includes('Try checking their Champion Mastery') && (
              <button
                type="button"
                onClick={() => {
                  setMode('Mastery');
                  handleSubmit(new Event('submit'));
                }}
                className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline focus:outline-none"
              >
                Switch to Mastery View
              </button>
            )}
            {mode === 'Mastery' && error.includes('Try checking their Recent Games') && (
              <button
                type="button"
                onClick={() => {
                  setMode('Recent');
                  handleSubmit(new Event('submit'));
                }}
                className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline focus:outline-none"
              >
                Switch to Recent Games View
              </button>
            )}
            {error.includes('Too many requests') && (
              <span className="mt-1 text-xs text-gray-600">
                Our servers are experiencing high traffic. Please wait a moment and try again.
              </span>
            )}
            {error.includes('Network error') && (
              <button
                type="button"
                onClick={() => handleSubmit(new Event('submit'))}
                className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline focus:outline-none"
              >
                Retry Request
              </button>
            )}
          </div>
        </div>
      )}

      {loading && (
        <div className="flex flex-col items-center justify-center mb-8">
          <div className="flex space-x-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
            <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse delay-150"></div>
            <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse delay-300"></div>
          </div>
          <span className="mt-2 text-gray-300">Fetching champion updates...</span>
        </div>
      )}

      {!error && hasSearched && championData.length === 0 && mode === 'Mastery' && (
        <div className="flex items-center justify-center p-4 mb-4 text-gray-800 bg-gray-100 rounded-lg">
          <span className="text-sm font-medium">No changes since you last played these champions.</span>
        </div>
      )}

      {patchNotes && Object.keys(patchNotes).length > 0 && (
        <div className="space-y-6 max-w-4xl mx-auto">
          {/* Overall Summary Section */}
          {patchNotes.summary && (
            <div className="bg-gray-800 rounded-lg p-6 shadow-lg mb-6">
              <h2 className="text-xl font-semibold mb-4 text-white">Changes Overview</h2>
              <div className="text-gray-300 whitespace-pre-wrap prose prose-invert max-w-none">
                {patchNotes.summary}
              </div>
            </div>
          )}

          {/* Individual Champion Changes */}
          {patchNotes.filteredChanges && Object.keys(patchNotes.filteredChanges).length > 0 && (
            <div className="space-y-6">
              {Object.entries(patchNotes.filteredChanges).map(([champion, changes]) => (
                <div key={champion} className="space-y-4">
                  <h3 className="text-lg font-semibold text-white">{champion}</h3>
                  {changes.length === 0 ? (
                    <div className="text-gray-400 text-sm">
                      No changes since you last played this champion
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {changes.map((change, index) => (
                      <div key={`${champion}-${index}`} className={`
                        bg-gray-800 rounded-lg p-4 shadow-lg
                        ${change['Change Type']?.toLowerCase() === 'buff' ? 'border-l-4 border-green-500' : 
                          change['Change Type']?.toLowerCase() === 'nerf' ? 'border-l-4 border-red-500' : 
                          'border-l-4 border-yellow-500'}
                      `}>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-400 text-sm">
                            {change.Date ? new Date(change.Date).toLocaleDateString() : ''}
                          </span>
                          <span className={`
                            px-2 py-1 rounded text-sm font-medium
                            ${change['Change Type']?.toLowerCase() === 'buff' ? 'bg-green-500/20 text-green-400' : 
                              change['Change Type']?.toLowerCase() === 'nerf' ? 'bg-red-500/20 text-red-400' : 
                              'bg-yellow-500/20 text-yellow-400'}
                          `}>
                            {change['Change Type']}
                          </span>
                        </div>
                        <div className="text-gray-300 text-sm">
                          {change['Change Description']}
                        </div>
                      </div>
                    ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SummonerPage;
